import {addDays, differenceInCalendarDays, format, startOfDay} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'

import {parseApiDate, toApiDateString} from '../jobs/job-schedule-format'
import {driversToNotifyLabel} from './driver-send-status'
import {scheduleKindLabel} from './job-schedule'
import {overlappingBoardBookings} from './resource-schedule-conflict'
import {
    type AllocationStatus,
    bookingCoversDay,
    type DispatchAssignment,
    type DispatchTimelineData,
    type JobAllocation,
    type JobRequirement,
    type RequirementKind,
    type UnassignedJob,
} from './types'

/**
 * Day offset of calendar today relative to the board window start.
 * Live week boards anchor dayOffset 0 at Monday; attention always means today.
 */
export function attentionTodayDayOffset(
    data: DispatchTimelineData,
    today: Date = businessCalendarDate(),
): number {
    if (data.scheduleWindowStart == null) {
        return 0
    }
    return differenceInCalendarDays(
        startOfDay(today),
        startOfDay(parseApiDate(data.scheduleWindowStart)),
    )
}

/** Whether a live board window contains calendar today and can represent the global count. */
export function canProvideCompleteAttentionCount(
    data: DispatchTimelineData,
    today: Date = businessCalendarDate(),
): boolean {
    if (data.scheduleWindowStart == null || data.scheduleWindowEnd == null) {
        return false
    }
    return (
        attentionTodayDayOffset(data, today) >= 0 &&
        differenceInCalendarDays(parseApiDate(data.scheduleWindowEnd), startOfDay(today)) >= 0
    )
}

function nextApiDateString(day: string): string {
    return toApiDateString(addDays(parseApiDate(day), 1))
}

export function driverCoverageDays(
    job: Pick<UnassignedJob, 'schedule'>,
    today: Date = businessCalendarDate(),
): {from: string; until: string | null} | null {
    const schedule = job.schedule
    if (!schedule) {
        return null
    }

    const todayString = toApiDateString(today)
    const from = schedule.startDate < todayString ? todayString : schedule.startDate
    if (schedule.endDate != null && schedule.endDate < from) {
        return {from: schedule.endDate, until: schedule.endDate}
    }

    return {from, until: schedule.endDate}
}

function driverCountForRequirement(job: UnassignedJob, requirementId: string, today: Date): number {
    const bookings = job.allocations.filter((entry) => entry.requirementId === requirementId)
    const range = driverCoverageDays(job, today)
    if (
        range == null ||
        bookings.every((booking) => bookingCoversWholeJob(booking.days, job.schedule))
    ) {
        return bookings.length
    }
    // Minimum coverage falls on the range start or the day after a booking ends.
    const checkDays = [
        range.from,
        ...bookings
            .map((booking) =>
                booking.days?.until != null ? nextApiDateString(booking.days.until) : null,
            )
            .filter(
                (day): day is string =>
                    day != null && day > range.from && (range.until == null || day <= range.until),
            ),
    ]
    return Math.min(
        ...checkDays.map(
            (day) => bookings.filter((booking) => bookingCoversDay(booking.days, day)).length,
        ),
    )
}

export function allocationCountForRequirement(
    job: UnassignedJob,
    requirementId: string,
    today: Date = businessCalendarDate(),
): number {
    const requirement = job.requirements.find((entry) => entry.id === requirementId)
    const fromAllocations = job.allocations.filter(
        (entry) => entry.requirementId === requirementId,
    ).length

    // Workers (and unique equipment once allocated) are instance-based — count cards,
    // not the board's stale assignedQuantity from load time.
    if (requirement?.kind === 'worker') {
        return driverCountForRequirement(job, requirementId, today)
    }

    const onSite = requirement?.onSiteQuantity ?? 0
    if (fromAllocations > 0) {
        return fromAllocations + onSite
    }

    // Stock equipment has no instance allocations; fulfillment is quantity-only.
    return (requirement?.assignedQuantity ?? 0) + onSite
}

/**
 * After local instance assign/remove, keep requirement quantities and status in sync
 * so headings and empty picker slots match the allocation cards.
 */
export function syncInstanceFulfillment(
    previousJob: UnassignedJob,
    nextJob: UnassignedJob,
): UnassignedJob {
    const requirements = nextJob.requirements.map((requirement) => {
        const fromAllocations = nextJob.allocations.filter(
            (entry) => entry.requirementId === requirement.id,
        ).length

        if (requirement.kind === 'worker') {
            return {...requirement, assignedQuantity: fromAllocations}
        }

        const wasInstanceTracked = previousJob.allocations.some(
            (entry) => entry.requirementId === requirement.id,
        )
        // Unique assets are mirrored into allocations; stock is quantity-only and
        // only changes via stock delta + board refetch.
        if (fromAllocations > 0 || wasInstanceTracked) {
            return {...requirement, assignedQuantity: fromAllocations}
        }

        return requirement
    })

    return {
        ...nextJob,
        requirements,
        // Force recompute from allocations — board status from load is stale after edits.
        allocationStatus: undefined,
    }
}

export function isRequirementSatisfied(
    job: UnassignedJob,
    requirement: JobRequirement,
    today: Date = businessCalendarDate(),
): boolean {
    return allocationCountForRequirement(job, requirement.id, today) >= requirement.quantity
}

export function bookingCoversWholeJob(
    days: JobAllocation['days'],
    schedule: UnassignedJob['schedule'],
): boolean {
    if (days == null || schedule == null) {
        return true
    }

    return (
        days.from <= schedule.startDate &&
        (days.until == null || (schedule.endDate != null && days.until >= schedule.endDate))
    )
}

export function bookingDaysLabel(
    days: JobAllocation['days'],
    schedule: UnassignedJob['schedule'],
): string | undefined {
    if (days == null || bookingCoversWholeJob(days, schedule)) {
        return undefined
    }

    const from = parseApiDate(days.from)
    const farApart =
        days.until == null || differenceInCalendarDays(parseApiDate(days.until), from) >= 7
    const dayFormat = farApart ? 'EEE MMM d' : 'EEE'
    if (days.until == null) {
        return `From ${format(from, dayFormat)}`
    }
    if (days.until === days.from) {
        return `${format(from, dayFormat)} only`
    }

    return `${format(from, dayFormat)}–${format(parseApiDate(days.until), dayFormat)}`
}

export function pendingAllocations(
    baselineAllocations: JobAllocation[],
    draftAllocations: JobAllocation[],
): JobAllocation[] {
    const baselineKeys = new Set(baselineAllocations.map((allocation) => allocationKey(allocation)))
    return draftAllocations.filter((allocation) => !baselineKeys.has(allocationKey(allocation)))
}

function allocationKey(allocation: JobAllocation): string {
    return `${allocation.requirementId}:${allocation.resourceId}`
}

export function driverAllocations(job: UnassignedJob): JobAllocation[] {
    return job.allocations.filter((entry) => entry.kind === 'worker')
}

export function isDriverAllocationNotified(allocation: JobAllocation): boolean {
    return allocation.notifiedAt != null
}

export function needsDriverNotification(allocation: JobAllocation): boolean {
    return (
        allocation.kind === 'worker' &&
        !isDriverAllocationNotified(allocation) &&
        !allocation.isPlaceholder
    )
}

export function unnotifiedDriverAllocations(job: UnassignedJob): JobAllocation[] {
    return driverAllocations(job).filter(needsDriverNotification)
}

export function notifiesOnlyDrivers(
    allocations: JobAllocation[],
    requirements: JobRequirement[] | undefined,
): boolean {
    const drivers = new Map((requirements ?? []).map((entry) => [entry.id, entry.isDriver]))
    return allocations
        .filter(needsDriverNotification)
        .every((allocation) => drivers.get(allocation.requirementId) ?? true)
}

export function pendingDriverNotificationCount(job: UnassignedJob): number {
    return unnotifiedDriverAllocations(job).length
}

export function allAssignedDriversNotified(job: UnassignedJob): boolean {
    return !driverAllocations(job).some(needsDriverNotification)
}

export function isJobDispatched(job: UnassignedJob): boolean {
    return computeAllocationStatus(job) === 'ready' && allAssignedDriversNotified(job)
}

export function computeAllocationStatus(job: UnassignedJob): AllocationStatus {
    if (job.allocationStatus != null) {
        return job.allocationStatus
    }
    if (job.requirements.length === 0 || job.allocations.length === 0) {
        return 'needs_resources'
    }
    if (job.requirements.every((requirement) => isRequirementSatisfied(job, requirement))) {
        return 'ready'
    }
    return 'partially_allocated'
}

function workerTypeNoun(label: string): string {
    const noun = label.trim().toLowerCase()
    if (!noun) return 'worker'

    return noun.endsWith('s') ? noun.slice(0, -1) : noun
}

function countWorkers(count: number, noun: string): string {
    return `${count} ${count === 1 ? noun : `${noun}s`}`
}

export function buildShortageSummaryStrings(job: UnassignedJob): string[] {
    const missingWorkers = new Map<string, number>()
    for (const requirement of job.requirements) {
        if (requirement.kind !== 'worker') continue
        const gap = requirement.quantity - allocationCountForRequirement(job, requirement.id)
        if (gap <= 0) continue
        const noun = workerTypeNoun(requirement.label)
        missingWorkers.set(noun, (missingWorkers.get(noun) ?? 0) + gap)
    }
    const missingEquipment = job.requirements
        .filter((requirement) => requirement.kind === 'rental_asset')
        .reduce(
            (summary, requirement) => {
                const gap =
                    requirement.quantity - allocationCountForRequirement(job, requirement.id)
                if (gap <= 0) {
                    return summary
                }
                return {
                    types: summary.types + 1,
                    units: summary.units + gap,
                }
            },
            {types: 0, units: 0},
        )

    const summaries: string[] = []
    if (missingWorkers.size > 0) {
        summaries.push(
            `Missing ${[...missingWorkers].map(([noun, count]) => countWorkers(count, noun)).join(' · ')}`,
        )
    }
    if (missingEquipment.types > 0) {
        summaries.push(
            missingEquipment.types === 1
                ? `Missing ${missingEquipment.units} equipment`
                : `Missing ${missingEquipment.types} equipment types`,
        )
    }
    return summaries
}

/** Total crew/equipment counts for board rows — stable as assignments change. */
export function buildRequirementCategorySummaryStrings(requirements: JobRequirement[]): string[] {
    const totalWorkers = new Map<string, number>()
    for (const requirement of requirements) {
        if (requirement.kind !== 'worker' || requirement.quantity <= 0) continue
        const noun = workerTypeNoun(requirement.label)
        totalWorkers.set(noun, (totalWorkers.get(noun) ?? 0) + requirement.quantity)
    }
    const equipmentRequirements = requirements.filter(
        (requirement) => requirement.kind === 'rental_asset',
    )
    const equipmentTypes = equipmentRequirements.length
    const equipmentUnits = equipmentRequirements.reduce(
        (total, requirement) => total + requirement.quantity,
        0,
    )

    const summaries: string[] = []
    if (totalWorkers.size > 0) {
        summaries.push(
            [...totalWorkers].map(([noun, count]) => countWorkers(count, noun)).join(' · '),
        )
    }
    if (equipmentTypes > 0) {
        summaries.push(
            equipmentTypes === 1
                ? equipmentUnits === 1
                    ? '1 equipment'
                    : `${equipmentUnits} equipment`
                : `${equipmentTypes} equipment types`,
        )
    }
    return summaries
}

export function buildBoardRequirementSummaries(job: UnassignedJob): string[] {
    return [
        ...buildRequirementCategorySummaryStrings(job.requirements),
        scheduleKindLabel(job.scheduleKind),
    ]
}

/** Fallback when committed rows do not carry explicit requirements. */
export function inferRequirementsFromAllocations(allocations: JobAllocation[]): JobRequirement[] {
    const requirements: JobRequirement[] = []
    const driverCount = allocations.filter((entry) => entry.kind === 'worker').length
    if (driverCount > 0) {
        requirements.push({
            id: 'inferred-drivers',
            kind: 'worker',
            label: 'Drivers',
            quantity: driverCount,
        })
    }

    const equipmentCounts = new Map<string, number>()
    for (const entry of allocations) {
        if (entry.kind !== 'rental_asset') {
            continue
        }
        equipmentCounts.set(
            entry.requirementId,
            (equipmentCounts.get(entry.requirementId) ?? 0) + 1,
        )
    }

    let equipmentIndex = 0
    for (const quantity of equipmentCounts.values()) {
        requirements.push({
            id: `inferred-equipment-${equipmentIndex}`,
            kind: 'rental_asset',
            label: 'Equipment',
            quantity,
        })
        equipmentIndex += 1
    }

    return requirements
}

export function buildRequirementSummaries(job: UnassignedJob): string[] {
    const summaries = buildShortageSummaryStrings(job)
    if (summaries.length === 0) {
        const unnotifiedDrivers = pendingDriverNotificationCount(job)
        if (unnotifiedDrivers > 0) {
            summaries.push(
                driversToNotifyLabel(
                    unnotifiedDrivers,
                    notifiesOnlyDrivers(job.allocations, job.requirements),
                ),
            )
        }
    }
    summaries.push(scheduleKindLabel(job.scheduleKind))
    return summaries
}

export function allocationStatusLabel(status: AllocationStatus): string {
    switch (status) {
        case 'needs_resources':
            return 'Needs resources'
        case 'partially_allocated':
            return 'Partially allocated'
        case 'ready':
            return 'Ready'
    }
}

export function isJobReadyForTimeline(job: UnassignedJob): boolean {
    return computeAllocationStatus(job) === 'ready'
}

/** Applies a server-provided notification timestamp to a worker allocation. */
export function applyDriverNotification(
    data: DispatchTimelineData,
    jobId: string,
    resourceId: string,
    notifiedAt: number,
): DispatchTimelineData {
    const jobIndex = data.unassignedJobs.findIndex((entry) => entry.id === jobId)
    if (jobIndex < 0) {
        return data
    }

    const job = data.unassignedJobs[jobIndex]
    if (!job) {
        return data
    }

    const driver = job.allocations.find(
        (entry) => entry.kind === 'worker' && entry.resourceId === resourceId,
    )
    if (!driver || isDriverAllocationNotified(driver)) {
        return data
    }

    const updatedJob: UnassignedJob = {
        ...job,
        allocations: job.allocations.map((entry) =>
            entry.kind === 'worker' && entry.resourceId === resourceId
                ? {...entry, notifiedAt}
                : entry,
        ),
    }

    if (isJobDispatched(updatedJob)) {
        const assignment = commitJobToTimeline(updatedJob)

        return {
            ...data,
            unassignedJobs: data.unassignedJobs.filter((entry) => entry.id !== jobId),
            assignments: [...data.assignments, assignment],
        }
    }

    const unassignedJobs = [...data.unassignedJobs]
    unassignedJobs[jobIndex] = updatedJob
    return {...data, unassignedJobs}
}

export function commitJobToTimeline(
    job: UnassignedJob,
    options: {boardScenario?: UnassignedJob['boardScenario']} = {},
): DispatchAssignment {
    const primaryWorker = job.allocations.find((entry) => entry.kind === 'worker')
    return {
        id: `board-${job.id}`,
        jobCode: job.jobCode,
        label: job.label,
        customer: job.customer,
        customerId: job.customerId,
        dispatchYardId: job.dispatchYardId,
        ...(job.closerYardName ? {closerYardName: job.closerYardName} : {}),
        ...(job.outOfStateLabel ? {outOfStateLabel: job.outOfStateLabel} : {}),
        ...(job.partialPickup ? {partialPickup: job.partialPickup} : {}),
        ...(job.unconfirmedRentalCount ? {unconfirmedRentalCount: job.unconfirmedRentalCount} : {}),
        internalContactName: job.internalContactName,
        scheduleKind: job.scheduleKind,
        schedule: job.schedule,
        span: job.span!,
        sourceJobId: job.id,
        resourceId: primaryWorker?.resourceId ?? job.allocations[0]?.resourceId ?? job.id,
        allocations: job.allocations,
        requirements: job.requirements,
        allocationStatus: computeAllocationStatus(job),
        boardScenario: options.boardScenario ?? job.boardScenario,
    }
}

type AllocateResourceInput = {
    requirementId: string
    resourceId: string
    resourceName: string
    kind: RequirementKind
    days?: JobAllocation['days']
}

/** Removes a resource from one board job (queue or committed row). */
export function unassignResourceFromJob(
    data: DispatchTimelineData,
    jobId: string,
    resourceId: string,
): DispatchTimelineData {
    const unassignedJobs = data.unassignedJobs.map((job) => {
        if (job.id !== jobId) {
            return job
        }
        if (!job.allocations.some((allocation) => allocation.resourceId === resourceId)) {
            return job
        }
        return syncInstanceFulfillment(job, {
            ...job,
            allocations: job.allocations.filter(
                (allocation) => allocation.resourceId !== resourceId,
            ),
        })
    })

    const assignments = data.assignments.map((assignment) => {
        const sourceJobId = assignment.sourceJobId ?? assignment.id
        if (sourceJobId !== jobId) {
            return assignment
        }

        const allocations = (assignment.allocations ?? []).filter(
            (allocation) => allocation.resourceId !== resourceId,
        )
        if (allocations.length === (assignment.allocations ?? []).length) {
            return assignment
        }

        const next: DispatchAssignment = {
            ...assignment,
            allocations,
        }
        if (assignment.resourceId === resourceId) {
            next.resourceId = allocations[0]?.resourceId ?? assignment.id
        }
        return next
    })

    return {...data, unassignedJobs, assignments}
}

/**
 * Job IDs that currently hold `resourceId` on a schedule overlapping `targetJob`.
 * Used so assign can clear those bookings before (or while) assigning here.
 */
export function overlappingConflictJobIds(
    resourceId: string,
    targetJob: UnassignedJob,
    data: DispatchTimelineData,
): string[] {
    return overlappingBoardBookings(resourceId, targetJob, data).map((booking) => booking.jobId)
}

export function allocateResourceToJob(
    data: DispatchTimelineData,
    jobId: string,
    input: AllocateResourceInput,
): DispatchTimelineData {
    const jobIndex = data.unassignedJobs.findIndex((entry) => entry.id === jobId)
    if (jobIndex < 0) {
        return data
    }

    const job = data.unassignedJobs[jobIndex]
    if (!job) {
        return data
    }
    const requirement = job.requirements.find((entry) => entry.id === input.requirementId)
    if (!requirement || requirement.kind !== input.kind) {
        return data
    }

    if (isRequirementSatisfied(job, requirement)) {
        return data
    }

    const alreadyAllocated = job.allocations.some((entry) => entry.resourceId === input.resourceId)
    if (alreadyAllocated) {
        return data
    }

    const placeholder = data.resources.find(
        (resource) => resource.id === input.resourceId,
    )?.isPlaceholder
    const allocation: JobAllocation = {
        requirementId: input.requirementId,
        resourceId: input.resourceId,
        resourceName: input.resourceName,
        kind: input.kind,
        ...(input.days ? {days: input.days} : {}),
        ...(input.kind === 'worker' && placeholder ? {isPlaceholder: true} : {}),
    }

    const updatedJob = syncInstanceFulfillment(job, {
        ...job,
        allocations: [...job.allocations, allocation],
    })

    const unassignedJobs = [...data.unassignedJobs]
    unassignedJobs[jobIndex] = updatedJob
    return {...data, unassignedJobs}
}

/**
 * Assigns resources to a job and clears them from any overlapping board bookings
 * so a confirm-to-reassign cannot leave the unit double-booked.
 */
export function allocateResourcesToJob(
    data: DispatchTimelineData,
    jobId: string,
    inputs: AllocateResourceInput[],
): DispatchTimelineData {
    const targetJob = data.unassignedJobs.find((entry) => entry.id === jobId)
    let next = data
    if (targetJob != null) {
        // Only equipment is released from overlapping jobs.
        for (const input of inputs.filter((entry) => entry.kind !== 'worker')) {
            for (const conflictJobId of overlappingConflictJobIds(
                input.resourceId,
                targetJob,
                next,
            )) {
                next = unassignResourceFromJob(next, conflictJobId, input.resourceId)
            }
        }
    }

    return inputs.reduce((current, input) => allocateResourceToJob(current, jobId, input), next)
}

export function replaceAllocationsForJob(
    data: DispatchTimelineData,
    jobId: string,
    allocations: JobAllocation[],
): DispatchTimelineData {
    const jobIndex = data.unassignedJobs.findIndex((entry) => entry.id === jobId)
    if (jobIndex < 0) {
        return data
    }

    const job = data.unassignedJobs[jobIndex]
    if (!job) {
        return data
    }

    const unassignedJobs = [...data.unassignedJobs]
    unassignedJobs[jobIndex] = syncInstanceFulfillment(job, {
        ...job,
        allocations,
    })
    return {...data, unassignedJobs}
}

export function formatAllocationNames(allocations: JobAllocation[]): string {
    return allocations.map((entry) => entry.resourceName).join(', ')
}
